import { format, isSameDay } from 'date-fns';
import { fr } from 'date-fns/locale';

const WEEKDAY_LABELS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
const MONTHLY_ORDINAL_LABELS = {
    1: '1er',
    2: '2e',
    3: '3e',
    4: '4e',
    '-1': 'dernier',
};

const joinFrenchList = (items) => {
    if (!Array.isArray(items) || !items.length) return '';
    if (items.length === 1) return items[0];
    if (items.length === 2) return `${items[0]} et ${items[1]}`;
    return `${items.slice(0, -1).join(', ')} et ${items[items.length - 1]}`;
};

const getRecurringWeekdayLabels = (weekdays) => {
    if (!Array.isArray(weekdays)) return [];
    return weekdays
        .map((weekday) => WEEKDAY_LABELS[Number(weekday)])
        .filter(Boolean);
};

const sortMonthlyWeeks = (weekList) => {
    return [...new Set(weekList.map(Number).filter(Number.isInteger))]
        .sort((a, b) => {
            const rankA = a === -1 ? 99 : a;
            const rankB = b === -1 ? 99 : b;
            return rankA - rankB;
        });
};

const formatMonthlyWeekListLabel = (weekList, weekdayLabels) => {
    if (!Array.isArray(weekList) || !weekList.length || !weekdayLabels.length) return '';
    const normalizedWeeks = sortMonthlyWeeks(weekList);
    const weekdayLabel = weekdayLabels[0];
    if (normalizedWeeks.length === 2 && normalizedWeeks[0] === 1 && normalizedWeeks[1] === 2) {
        return `Les 2 premiers ${weekdayLabel}s du mois`;
    }
    if (normalizedWeeks.length === 3 && normalizedWeeks[0] === 1 && normalizedWeeks[1] === 2 && normalizedWeeks[2] === 3) {
        return `Les 3 premiers ${weekdayLabel}s du mois`;
    }
    if (normalizedWeeks.length === 4 && normalizedWeeks[0] === 1 && normalizedWeeks[1] === 2 && normalizedWeeks[2] === 3 && normalizedWeeks[3] === 4) {
        return `Les 4 premiers ${weekdayLabel}s du mois`;
    }
    const ordinalLabels = normalizedWeeks
        .map((week) => MONTHLY_ORDINAL_LABELS[week])
        .filter(Boolean);
    if (ordinalLabels.length) {
        return `Chaque ${joinFrenchList(ordinalLabels)} ${weekdayLabel} du mois`;
    }
    return '';
};

export const formatDateWithOptionalTime = (jsDate) => {
    if (!jsDate) return '-';
    if (!(jsDate instanceof Date) || isNaN(jsDate.getTime())) return String(jsDate);
    
    // Check if the date has a non-midnight time component
    const isMidnight = jsDate.getHours() === 0 && jsDate.getMinutes() === 0;
    
    return format(
        jsDate, 
        isMidnight ? 'dd/MM/yyyy' : 'dd/MM/yyyy HH:mm', 
        { locale: fr }
    );
};

export const formatDateOnly = (jsDate) => {
    if (!jsDate) return '-';
    if (!(jsDate instanceof Date) || isNaN(jsDate.getTime())) return String(jsDate);
    return format(jsDate, 'dd/MM/yyyy', { locale: fr });
};

export const isSameCalendarDay = (a, b) => {
    if (!(a instanceof Date) || isNaN(a?.getTime?.()) || !(b instanceof Date) || isNaN(b?.getTime?.())) return false;
    return isSameDay(a, b);
};

export const formatEventRecurrenceLabel = (event) => {
    if (!event?.isRecurring) return '';

    if (event.recurrenceFrequency === 'weekly') {
        const weekdayLabels = getRecurringWeekdayLabels(event.recurrenceWeekdays);
        return weekdayLabels.length ? `Chaque ${joinFrenchList(weekdayLabels)}` : 'Chaque semaine';
    }

    if (event.recurrenceFrequency === 'monthly') {
        const weekdayLabels = getRecurringWeekdayLabels(event.recurrenceWeekdays);
        const multiWeekLabel = formatMonthlyWeekListLabel(event.recurrenceWeekOfMonthList, weekdayLabels);
        if (multiWeekLabel) {
            return multiWeekLabel;
        }
        const ordinalLabel = MONTHLY_ORDINAL_LABELS[event.recurrenceWeekOfMonth];
        if (ordinalLabel && weekdayLabels.length) {
            return `Chaque ${ordinalLabel} ${weekdayLabels[0]} du mois`;
        }
        if (event.recurrenceDayOfMonth) {
            return `Chaque ${event.recurrenceDayOfMonth} du mois`;
        }
        return 'Chaque mois';
    }

    return 'Événement récurrent';
};
